/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, InputNumber } from "antd";

interface InnerCardProps {
  laborRate: number;
  totalCost: number;
  lineItems?: boolean;
  setGroupedData: React.Dispatch<React.SetStateAction<any[]>>;
  serviceLineId: string;
  groupedData: any[];
  totalPrice: number;
  gmValue?: number; // Optional prop for GM value
}

export default function InnerCard(props: InnerCardProps) {
  const {
    laborRate,
    totalCost,
    lineItems,
    setGroupedData,
    serviceLineId,
    totalPrice,
    gmValue,
  } = props;

  let grossMargin = 0;
  if (totalPrice && totalCost) {
    grossMargin = parseFloat(
      Number(((totalPrice - totalCost) / totalPrice) * 100).toFixed(1)
    );
  } else if (gmValue) {
    grossMargin = parseFloat(Number(gmValue).toFixed(1));
  } else {
    grossMargin = 0;
  }

  return (
    <Card className="innerCard">
      {!lineItems && (
        <div className="innerCardBox">
          <p>Labor rate:</p>
          <h3>
            <InputNumber
              className="serviceLineInput"
              value={laborRate}
              placeholder="0"
              addonBefore="$"
              min={0}
              controls={false}
              style={{ background: laborRate > 0 ? "transparent" : "#FFF9D9" }}
              addonAfter={<span>/hr</span>}
              onChange={(e) => {
                const value = e;
                if (!isNaN(Number(value))) {
                  setGroupedData((prevData) => {
                    return prevData.map((serviceLine) => {
                      if (serviceLine.service_line_id === serviceLineId) {
                        // 1) Update service groups
                        const updatedGroups =
                          serviceLine?.service_groups?.map(
                            (group: { id: string; services: any[] }) => {
                              const updatedServices = group?.services.map(
                                (service: any) => {
                                  let totalMaterialCost = 0;

                                  if (service?.material?.length > 0) {
                                    service.material.forEach((mat: any) => {
                                      if (mat?.unit_cost && mat?.use_rate) {
                                        const singleTotalNeed =
                                          (service?.qty ?? 0) / mat.use_rate ||
                                          0;
                                        totalMaterialCost +=
                                          singleTotalNeed * mat?.unit_cost || 0;
                                      }
                                    });
                                  }

                                  let laborHrs = 1;
                                  if (service?.frequency === "one_time") {
                                    laborHrs = service?.prod_rate
                                      ? (service?.qty ?? 0) / service?.prod_rate
                                      : 0;
                                  } else {
                                    const HourPerVisit =
                                      service?.qty / service?.prod_rate || 0;
                                    laborHrs =
                                      HourPerVisit * service?.visit || 0;
                                  }

                                  const totalLaborCost =
                                    laborHrs * Number(value) || 0;
                                  const totalCost =
                                    totalLaborCost + totalMaterialCost || 0;
                                  const totalPrice =
                                    (totalCost * 100) /
                                    (100 - (service?.gross_margin_perc || 0));

                                  return {
                                    ...service,
                                    total_cost: totalCost,
                                    total_price: totalPrice,
                                  };
                                }
                              );

                              // ✅ roll up totals for the group
                              const groupTotals = updatedServices.reduce(
                                (sum, svc) => {
                                  sum.cost += svc.total_cost || 0;
                                  sum.price += svc.total_price || 0;
                                  return sum;
                                },
                                { cost: 0, price: 0 }
                              );

                              return {
                                ...group,
                                services: updatedServices,
                                total_cost: groupTotals.cost,
                                total_price: groupTotals.price,
                              };
                            }
                          ) || [];

                        // 2) Update standalone services
                        const updatedServices =
                          serviceLine?.services?.map((service: any) => {
                            const unitCost =
                              service?.material?.reduce(
                                (sum: any, item: { unit_cost: number }) =>
                                  sum + item.unit_cost,
                                0
                              ) || 0;

                            const useRate =
                              service?.material?.reduce(
                                (sum: any, item: { use_rate: number }) =>
                                  sum + item.use_rate,
                                0
                              ) || 0;

                            const totalNeed = service?.qty / useRate || 0;
                            const laborHrs =
                              service?.qty / service?.prod_rate || 0;

                            const totalLaborCost =
                              laborHrs * Number(value) || 0;
                            const totalMaterialCost = totalNeed * unitCost || 0;
                            const totalSCost =
                              totalLaborCost + totalMaterialCost || 0;
                            const totalSPrice =
                              (totalSCost * 100) /
                              (100 - (service?.gross_margin_perc || 0));

                            return {
                              ...service,
                              total_cost: totalSCost,
                              total_price: totalSPrice,
                            };
                          }) || [];

                        // 3) Roll up totals for the entire serviceLine
                        const totals = { cost: 0, price: 0 };

                        updatedGroups.forEach(
                          (g: { total_cost: number; total_price: number }) => {
                            totals.cost += g.total_cost || 0;
                            totals.price += g.total_price || 0;
                          }
                        );

                        updatedServices.forEach(
                          (s: { total_cost: number; total_price: number }) => {
                            totals.cost += s.total_cost || 0;
                            totals.price += s.total_price || 0;
                          }
                        );

                        return {
                          ...serviceLine,
                          labor_rate: Number(value),
                          service_groups: updatedGroups,
                          services: updatedServices,
                          total_cost: totals.cost,
                          total_price: totals.price,
                        };
                      }
                      return serviceLine;
                    });
                  });
                }
              }}
            />
          </h3>
        </div>
      )}
      <div className="innerCardBox">
        <p>Total cost:</p>
        <h3>${Number(totalCost?.toFixed(2)).toLocaleString()}</h3>
      </div>
      <div className="innerCardBox">
        <p>Total price:</p>
        <h3>${Number(totalPrice?.toFixed(2)).toLocaleString()}</h3>
      </div>
      <div className="innerCardBox">
        <p>GM:</p>
        <h3>
          <InputNumber
            className="serviceLineInput"
            addonAfter={<span>%</span>}
            max={99}
            min={0}
            controls={false}
            placeholder="0"
            value={grossMargin ?? ""}
            style={{ background: grossMargin > 0 ? "transparent" : "#FFF9D9" }}
            onChange={(e) => {
              const value = e;

              setGroupedData((prevData: any) => {
                return prevData.map((serviceLine: any) => {
                  if (serviceLine?.service_line_id === serviceLineId) {
                    // 1) update service groups + services inside them
                    const updatedGroups =
                      serviceLine?.service_groups?.map(
                        (group: { id: string; services: any[] }) => {
                          const updatedServices = group?.services.map(
                            (service: any) => {
                              const totalPrice =
                                (service?.total_cost * 100) /
                                (100 - Number(value) || 0);
                              return {
                                ...service,
                                gross_margin_perc: value,
                                total_price: totalPrice,
                              };
                            }
                          );

                          // ✅ roll up group totals
                          const groupTotals = updatedServices.reduce(
                            (sum, svc) => {
                              sum.cost += svc.total_cost || 0;
                              sum.price += svc.total_price || 0;
                              return sum;
                            },
                            { cost: 0, price: 0 }
                          );

                          return {
                            ...group,
                            gross_margin_perc: value,
                            services: updatedServices,
                            total_cost: groupTotals.cost,
                            total_price: groupTotals.price,
                          };
                        }
                      ) || [];

                    // 2) update standalone services
                    const updatedServices =
                      serviceLine?.services?.map((service: any) => {
                        const totalPrice =
                          (service?.total_cost * 100) /
                          (100 - Number(value) || 0);
                        return {
                          ...service,
                          gross_margin_perc: value,
                          total_price: totalPrice,
                        };
                      }) || [];

                    // 3) update line items
                    const updatedLineItems =
                      serviceLine?.line_items?.map((item: any) => {
                        const totalPrice =
                          (item?.total_cost * 100) / (100 - Number(value) || 0);
                        return {
                          ...item,
                          gross_margin_perc: value,
                          total_price: totalPrice,
                        };
                      }) || [];

                    // 4) roll up totals for the whole serviceLine
                    const totals = {
                      cost: 0,
                      price: 0,
                    };

                    updatedGroups.forEach(
                      (g: { total_cost: number; total_price: number }) => {
                        totals.cost += g.total_cost || 0;
                        totals.price += g.total_price || 0;
                      }
                    );

                    updatedServices.forEach(
                      (s: { total_cost: number; total_price: number }) => {
                        totals.cost += s.total_cost || 0;
                        totals.price += s.total_price || 0;
                      }
                    );

                    updatedLineItems.forEach(
                      (li: { total_cost: number; total_price: number }) => {
                        totals.cost += li.total_cost || 0;
                        totals.price += li.total_price || 0;
                      }
                    );

                    return {
                      ...serviceLine,
                      gross_margin_perc: value,
                      service_groups: updatedGroups,
                      services: updatedServices,
                      line_items: updatedLineItems,
                      total_cost: totals.cost,
                      total_price: totals.price,
                    };
                  }
                  return serviceLine;
                });
              });
            }}
          />
        </h3>
      </div>
    </Card>
  );
}
